import { IconChevronChevronLeft } from '@doltech/icons/IconChevronChevronLeft';
import { IconChevronChevronRight } from '@doltech/icons/IconChevronChevronRight';
import { IconChevronPushChevronRight } from '@doltech/icons/IconChevronPushChevronRight';
import { Pagination, PaginationProps } from 'antd';
import * as React from 'react';
import styled from 'styled-components';

import { useFromScreens } from '../../hooks/useDeviceQuery.hook';
import { Button } from '../ButtonV2/ButtonV2';
import { colorsV2 } from '../colors-v2';
import { Typography } from '../DesignSystemV2/Typography';
import { IconPushBack } from './svg/IconPushBack';
import { InputContentDropdownSelect } from '../Input/InputContentDropdownSelect/InputContentDropdownSelect';
import { SelectOptionList } from '../Select/SelectOptionList/SelectOptionList';
import { DropdownItemItemLayout } from '../Select/DropdownItemItemLayout/DropdownItemItemLayout';
import { SelectDropdownItemContent } from '../Select/SelectDropdownItemContent/SelectDropdownItemContent';
import { SelectGreyStyleInputGeneral } from '../Select/SelectGreyStyleInputGeneral/SelectGreyStyleInputGeneral';
import { isNil } from 'lodash';

export const DEFAULT_PAGE_SIZE = 10;

const Main = styled.div`
  margin-top: 10px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content;
  &.has-prev {
    grid-template-columns: max-content minmax(0, 1fr);
  }
  &.has-next {
    grid-template-columns: minmax(0, 1fr) max-content;
  }
  &.has-prev.has-next {
    grid-template-columns: max-content minmax(0, 1fr) max-content;
  }
`;

const StyledDiv = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  > .ant-pagination {
    > .ant-pagination-options {
      display: none;
    }
    > .ant-pagination-item-active {
      background: ${colorsV2.primary100};
      border-radius: 50%;
      a {
        color: ${colorsV2.white100};
      }
      &:hover {
        background: ${colorsV2.primary80};
      }
      &:focus {
        background: ${colorsV2.primary100};
        color: ${colorsV2.white40};
      }
    }
    > .ant-pagination-item {
      margin-right: 4px;
      &:not(.ant-pagination-item-active) {
        background: ${colorsV2.gray10};
        border-radius: 50%;
        border: none;
        a {
          color: ${colorsV2.black100};
        }
        &:hover {
          background: ${colorsV2.gray20};
        }
        &:focus {
          background: ${colorsV2.gray40};
        }
      }
    }
    > .ant-pagination-next,
    .ant-pagination-prev {
      color: ${colorsV2.black100};
      border: none;
      border-radius: 50%;

      .ant-pagination-item-link {
        color: ${colorsV2.black100};
      }
      background: transparent;
      button {
        background: transparent;
        border: none;
        border-radius: 50%;
      }
      &:hover {
        background: ${colorsV2.gray40};
      }
      &:active {
        background: ${colorsV2.gray60};
      }
    }
  }
`;

export interface Props extends PaginationProps {
  numberPerPage: number;
  pageSizeChange?: (amount: number) => void;
  totalPage: number;
}

export const PaginationComponent = (props: Props) => {
  const {
    current,
    showSizeChanger = true,
    pageSizeChange,
    onChange,
    numberPerPage,
    totalPage,
    defaultCurrent,
    pageSizeOptions = ['10', '25', '50', '100'],
    hideOnSinglePage,
  } = props;
  const [, from776] = useFromScreens([0, 776]);
  const [value, setValue] = React.useState(
    numberPerPage < DEFAULT_PAGE_SIZE ? DEFAULT_PAGE_SIZE : numberPerPage
  );
  if (hideOnSinglePage && (totalPage === 1 || isNil(totalPage))) {
    return null;
  }

  if (from776) {
    return (
      <StyledDiv className="pagination-component">
        {showSizeChanger && (
          <SelectGreyStyleInputGeneral
            inputContent={
              <InputContentDropdownSelect
                text={`${value} per page`}
                placeholder="Select language"
              />
            }
            dropdownContent={
              <SelectOptionList>
                {pageSizeOptions?.map((item, index) => (
                  <DropdownItemItemLayout
                    key={index}
                    content={
                      <SelectDropdownItemContent>
                        <SelectDropdownItemContent.Text text={`${item} per page`} />
                      </SelectDropdownItemContent>
                    }
                    onClick={(e) => {
                      setValue(item);
                      pageSizeChange(+Number(item));
                    }}
                  />
                ))}
              </SelectOptionList>
            }
          />
        )}
        <Pagination key={numberPerPage?.toString()} {...props} />
      </StyledDiv>
    );
  }
  return (
    <PaginationMobile
      currentPage={defaultCurrent || current}
      onPageChange={onChange}
      totalPage={totalPage}
    />
  );
};

const CenterDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RowAction = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-items: center;
  gap: 8px;
`;

export const PaginationMobile = ({ currentPage, totalPage, onPageChange }) => {
  if (totalPage === 1) {
    return null;
  }
  if (currentPage === 1) {
    return (
      <Main className="pagination-component has-next">
        <CenterDiv>
          <Typography.Text variant="semi-bold/14">{`1 of ${totalPage}`}</Typography.Text>
        </CenterDiv>
        <RowAction>
          <Button.Primary
            onClick={() => onPageChange(currentPage + 1)}
            iconRight={<IconChevronChevronRight />}
          />
          <Button.Secondary
            variant="outline"
            onClick={() => onPageChange(totalPage)}
            iconRight={<IconChevronPushChevronRight />}
          />
        </RowAction>
      </Main>
    );
  }
  return (
    <Main className="pagination-component has-prev has-next">
      <RowAction>
        <Button.Secondary
          variant="outline"
          onClick={() => onPageChange(1)}
          iconRight={<IconPushBack />}
        />
        <Button.Primary
          onClick={() => onPageChange(currentPage - 1)}
          iconRight={<IconChevronChevronLeft />}
        />
      </RowAction>
      <CenterDiv>
        <Typography.Text variant="semi-bold/14">{`${currentPage} of ${totalPage}`}</Typography.Text>
      </CenterDiv>
      {currentPage < totalPage && (
        <RowAction>
          <Button.Primary
            onClick={() => onPageChange(currentPage + 1)}
            iconRight={<IconChevronChevronRight />}
          />
          <Button.Secondary
            variant="outline"
            onClick={() => onPageChange(totalPage)}
            iconRight={<IconChevronPushChevronRight />}
          />
        </RowAction>
      )}
    </Main>
  );
};
